/**
 * Per-route page metadata. Attached to a `RouteObject.handle` so
 * `PageTitleArea` (mounted inside `DashboardLayout`) can resolve the
 * active page's title + breadcrumbs via `useMatches()` without
 * maintaining a parallel registry.
 *
 * Dynamic titles (e.g. detail pages showing an entity name) override
 * via `usePageHeader({ title, breadcrumbs })` from the page itself.
 */
export interface RoutePageMeta {
  /** i18n key for the page title. */
  titleKey?: string;
  /** i18n namespace. Default: 'common'. */
  namespace?: string;
  /** Breadcrumbs rendered before the current page title. */
  breadcrumbs?: Array<{
    labelKey: string;
    namespace?: string;
    href?: string;
  }>;
  /** Render breadcrumbs but no H1. */
  hideTitle?: boolean;
}

/**
 * Runtime type guard for `RouteObject.handle` — `useMatches` types
 * `handle` as `unknown`, so we narrow it here before reading fields.
 */
export function isRoutePageMeta(value: unknown): value is RoutePageMeta {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  // A handle is considered RoutePageMeta if it has at least one of the
  // known optional fields. Empty objects don't qualify (would silently
  // hide other matches' metadata).
  return 'titleKey' in v || 'breadcrumbs' in v || 'hideTitle' in v;
}
